<template>
  <div class="row">
    <div class="col-lg-3">
      <h4>Available Jobs</h4>
      <div class="list-group">
        <div
          class="list-group-item text-center"
          @click="selectMarker(job)"
          :key="jobIndex"
          v-for="(job, jobIndex) in availableJobs"
        >
          <jobable-claim-reference :job="job"></jobable-claim-reference>
          <br />
          <job-label :job="job"></job-label>
          <template v-if="job.vehicle?.registration">
            <br />
            <strong>Vehicle</strong><br />
            {{ job.vehicle?.registration?.toUpperCase() }}
          </template>
          <br />
          <strong>Location</strong><br />
          {{ job.location }}
          <br />
          <strong>Date</strong><br />
          {{ job.date | date }}
          <div class="text-center" style="margin-top: 7px;">
            <div class="btn-group">
              <a :href="job.url" class="btn btn-xs btn-primary" target="_blank">
                View Claim
              </a>
              <button
                type="button"
                class="btn btn-xs"
                :class="{
                  'btn-primary': !selected(job),
                  'btn-danger': selected(job)
                }"
                @click.prevent="addToRun(job)"
              >
                {{ selected(job) ? 'Remove From Run' : 'Add to Run' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="col-lg-9">
      <div class="job-results">
        <div class="job-results-map" id="results-map"></div>
      </div>
    </div>
  </div>
</template>

<script>
import JobLabel from '@/components/Modules/Shared/JobLabel';
import JobableClaimReference from '@/components/Modules/Job/Children/JobableClaimReference';

export default {
  props: {
    jobs: {
      type: Array,
      required: true
    },
    value: {
      type: Array,
      required: true
    },
    run: {
      type: Object
    }
  },

  components: {
    JobLabel,
    JobableClaimReference
  },

  data() {
    return {
      map: null,
      initialCenter: {
        lat: 51.507,
        lng: 0.127
      },
      markers: []
    };
  },

  computed: {
    availableJobs() {
      return this.jobs.filter(job => {
        return (
          !job.completed_at && (!job.item || this.run?.id == job.item.run_id)
        );
      });
    },

    selectedJobs: {
      get() {
        return this.value;
      },
      set(values) {
        this.$emit('input', values);
      }
    }
  },

  mounted() {
    window.GoogleMapsLoader.load(google => {
      let center = this.calculateCenter();

      let options = {
        center: center,
        zoom: this.calculateZoom(google)
      };

      this.map = new google.maps.Map(
        document.getElementById('results-map'),
        options
      );

      this.setMarkers();
    });
  },

  methods: {
    addToRun(job) {
      const index = this.selectedJobs.findIndex(item => item.job_id === job.id);

      if (index > -1) {
        const values = [...this.selectedJobs];

        values.splice(index, 1);

        this.selectedJobs = values;
      } else {
        const values = [...this.selectedJobs];

        this.selectedJobs = [
          ...values,
          { job_id: job.id, sort_order: this.selectedJobs.length }
        ];
      }
    },

    selected(job) {
      const index = this.selectedJobs.findIndex(item => item.job_id == job.id);

      return index > -1;
    },

    setMarkers() {
      window.GoogleMapsLoader.load(google => {
        this.markers.forEach(marker => marker.setMap(null));

        this.availableJobs.forEach(job => {
          const marker = new google.maps.Marker({
            position: new google.maps.LatLng(job.latitude, job.longitude),
            title: `${job.type?.label} - ${job.location}`,
            label: {
              text: job.type?.name === 'delivery' ? 'D' : 'C',
              color: 'white',
              fontSize: '16px'
            },
            icon: {
              path:
                'M 12,2 C 8.1340068,2 5,5.1340068 5,9 c 0,5.25 7,13 7,13 0,0 7,-7.75 7,-13 0,-3.8659932 -3.134007,-7 -7,-7 z',
              anchor: new google.maps.Point(12, 17),
              fillOpacity: 1,
              fillColor: job.type?.name === 'delivery' ? '#56c3c4' : '#f0ad4e',
              strokeWeight: 2,
              strokeColor:
                job.type?.name === 'delivery' ? '#3aa6a7' : '#ea9113',
              scale: 2,
              labelOrigin: new google.maps.Point(12, 9)
            }
          });

          const infoWindow = new google.maps.InfoWindow({
            content: `${job.type?.label}<br>${job.location}`
          });

          marker.addListener('click', () => {
            infoWindow.open({
              anchor: marker,
              map: this.map
            });
          });

          marker.setMap(this.map);

          this.markers.push({
            marker,
            job
          });
        });
      });
    },

    redraw() {
      window.GoogleMapsLoader.load(google => {
        this.map.setCenter(this.calculateCenter());
        this.map.setZoom(this.calculateZoom(google));
      });
    },

    calculateCenter() {
      let i = 0;
      let latitude = 0;
      let longitude = 0;

      this.availableJobs.forEach(job => {
        latitude = latitude + job.latitude;
        longitude = longitude + job.longitude;
        i++;
      });

      return {
        lat: latitude / i,
        lng: longitude / i
      };
    },

    calculateZoom(google) {
      const bounds = new google.maps.LatLngBounds();

      this.availableJobs.forEach(job =>
        bounds.extend(new google.maps.LatLng(job.latitude, job.longitude))
      );

      const WORLD_DIM = { height: 256, width: 256 };
      const ZOOM_MAX = 15;

      function latRad(lat) {
        const sin = Math.sin((lat * Math.PI) / 180);
        const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;

        return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
      }

      function zoom(mapPx, worldPx, fraction) {
        return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
      }

      const ne = bounds.getNorthEast();
      const sw = bounds.getSouthWest();

      const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

      const lngDiff = ne.lng() - sw.lng();
      const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

      const el = document.getElementById('results-map');

      const latZoom = zoom(el.clientHeight - 30, WORLD_DIM.height, latFraction);
      const lngZoom = zoom(el.clientWidth - 30, WORLD_DIM.width, lngFraction);

      return Math.min(latZoom, lngZoom, ZOOM_MAX);
    },

    selectMarker(job) {
      window.GoogleMapsLoader.load(google => {
        const marker = this.markers.find(marker => marker.job?.id === job.id);

        if (marker) {
          new google.maps.event.trigger(marker.marker, 'click');
        }
      });
    },

    jobLabelClass(job) {
      switch (job.type?.name) {
        case 'hire_delivery':
        case 'client_delivery':
          return 'label-primary';

        default:
          return 'label-warning';
      }
    }
  },

  watch: {
    jobs: {
      handler() {
        if (this.map) {
          this.redraw();
          this.setMarkers();
        }
      },
      immediate: true
    }
  }
};
</script>

<style lang="scss">
.job-results {
  height: 100%;

  .job-results-map {
    height: 100%;
  }
}
</style>
